import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Search, MapPin, Stethoscope, ShieldCheck, ChevronLeft, MessageCircle,
  Loader2, CheckCircle2, AlertCircle, Lock, Mail, RotateCw,
} from 'lucide-react';
import { SearchableCombobox } from '../components/SearchableCombobox';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /claim — public 3-step flow for an existing scraped CNPJ to claim its profile.
 *   1. Search by name or phone
 *   2. WhatsApp 6-digit code (30 min)
 *   3. Email + password → create account → /{slug} (own doctor profile)
 */
export default function ClaimPage() {
  const navigate = useNavigate();
  const { applySession } = useAuth();
  const [step, setStep] = useState(1); // 1=search, 2=verify, 3=register

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Reivindicar perfil | BuscaDoutor';
  }, []);

  // ---- step 1: search
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    professionalName: '',
    specialtyId: '',
    specialtyName: '',
    state: '',
    stateName: '',
    city: '',
    phone: '',
    email: '',
  });
  const [contactSpecialties, setContactSpecialties] = useState([]);
  const [contactStates, setContactStates] = useState([]);
  const [contactCities, setContactCities] = useState([]);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactLoadingCities, setContactLoadingCities] = useState(false);
  const [contactSpecialtyOpen, setContactSpecialtyOpen] = useState(false);
  const [contactStateOpen, setContactStateOpen] = useState(false);
  const [contactCityOpen, setContactCityOpen] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!contactModalOpen || contactSpecialties.length || contactStates.length) return;

    const fetchContactOptions = async () => {
      setContactLoading(true);
      try {
        const [specialtiesRes, statesRes] = await Promise.all([
          axios.get(`${API}/public/specialties`),
          axios.get(`${API}/public/states`),
        ]);
        setContactSpecialties((specialtiesRes.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR')));
        setContactStates((statesRes.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR')));
      } catch {
        setContactSpecialties([]);
        setContactStates([]);
      } finally {
        setContactLoading(false);
      }
    };

    fetchContactOptions();
  }, [contactModalOpen, contactSpecialties.length, contactStates.length]);

  useEffect(() => {
    if (!contactForm.state) {
      setContactCities([]);
      return;
    }

    const fetchContactCities = async () => {
      setContactLoadingCities(true);
      try {
        const { data } = await axios.get(`${API}/public/cities?state=${contactForm.state}`);
        setContactCities((data || []).sort((a, b) => (a.city || '').localeCompare(b.city || '', 'pt-BR')));
      } catch {
        setContactCities([]);
      } finally {
        setContactLoadingCities(false);
      }
    };

    fetchContactCities();
  }, [contactForm.state]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setResults([]);
      setSearchError('');
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      setSearchError('');
      try {
        const { data } = await axios.post(`${API}/claim/search`, { query: query.trim() });
        setResults(data.results || []);
      } catch (e) {
        setSearchError(e?.response?.data?.detail || 'Erro ao buscar perfis');
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  // ---- step 2: verify
  const [selected, setSelected] = useState(null); // selected hit
  const [tokenId, setTokenId] = useState(null);
  const [contactMasked, setContactMasked] = useState('');
  const [expiresAt, setExpiresAt] = useState(null);
  const [otp, setOtp] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);

  // countdowns
  useEffect(() => {
    if (step !== 2 || !expiresAt) return undefined;
    const tick = () => {
      const remaining = Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
      setSecondsLeft(remaining);
    };
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, [step, expiresAt]);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const i = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(i);
  }, [resendCooldown]);

  const expiresLabel = useMemo(() => {
    if (!secondsLeft) return '00:00';
    const m = Math.floor(secondsLeft / 60).toString().padStart(2, '0');
    const s = (secondsLeft % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  }, [secondsLeft]);

  const requestCode = async (cnpjId) => {
    setVerifyError('');
    try {
      const { data } = await axios.post(`${API}/claim/request-code`, { cnpj_id: cnpjId });
      setTokenId(data.token_id);
      setContactMasked(data.contact_masked);
      setExpiresAt(data.expires_at);
      setResendCooldown(data.resend_cooldown_seconds || 60);
    } catch (e) {
      setVerifyError(e?.response?.data?.detail || 'Não foi possível enviar o código');
      throw e;
    }
  };

  const handleSelect = async (hit) => {
    if (!hit.has_phone) return;
    setSelected(hit);
    setStep(2);
    try {
      await requestCode(hit.id);
    } catch {
      // stay on step 2 with error visible
    }
  };

  const handleResend = async () => {
    if (!selected || resendCooldown > 0) return;
    setOtp('');
    await requestCode(selected.id);
  };

  // ---- step 3: register
  const [claimSession, setClaimSession] = useState(null);
  const [email, setEmail] = useState('');
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [registerError, setRegisterError] = useState('');

  const handleVerify = async (e) => {
    e?.preventDefault?.();
    if (otp.length !== 6) {
      setVerifyError('Digite os 6 dígitos do código');
      return;
    }
    setVerifying(true);
    setVerifyError('');
    try {
      const { data } = await axios.post(`${API}/claim/verify-code`, {
        token_id: tokenId,
        code: otp,
      });
      setClaimSession(data.claim_session);
      setStep(3);
    } catch (err) {
      setVerifyError(err?.response?.data?.detail || 'Código inválido');
    } finally {
      setVerifying(false);
    }
  };

  const handleFinalize = async (e) => {
    e?.preventDefault?.();
    setRegisterError('');
    if (pwd !== pwd2) {
      setRegisterError('As senhas não coincidem');
      return;
    }
    if (pwd.length < 8) {
      setRegisterError('A senha deve ter no mínimo 8 caracteres');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/claim/finalize`,
        { claim_session: claimSession, email: email.trim().toLowerCase(), password: pwd },
        { withCredentials: true }
      );
      // Hydrate AuthContext immediately (token + user) so DoctorProfilePage
      // recognizes the freshly-claimed doctor as the owner and shows the
      // "Editar perfil" button.
      await applySession({ access_token: data.access_token, user: data.user });
      navigate(data.redirect || `/medico/${data.cnpj_id}`);
    } catch (err) {
      setRegisterError(err?.response?.data?.detail || 'Falha ao criar conta');
    } finally {
      setSubmitting(false);
    }
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();

    if (contactForm.phone.replace(/\D/g, '').length !== 11) return;

    const text = [
      'Olá, BuscaDoutor. Preciso de ajuda para reivindicar meu perfil, mas não tenho um telefone cadastrado.',
      contactForm.professionalName ? `Nome profissional: ${contactForm.professionalName}` : '',
      contactForm.specialtyName ? `Especialidade: ${contactForm.specialtyName}` : '',
      contactForm.state ? `Estado: ${contactForm.stateName || contactForm.state} (${contactForm.state})` : '',
      contactForm.city ? `Cidade: ${contactForm.city}` : '',
      contactForm.phone ? `Telefone: ${contactForm.phone}` : '',
      contactForm.email ? `Email: ${contactForm.email}` : '',
      query ? `Busca realizada: ${query}` : '',
    ].filter(Boolean).join('\n');

    window.open(
      `https://wa.me/5571999375656?text=${encodeURIComponent(text)}`,
      '_blank',
      'noopener,noreferrer'
    );
    setContactModalOpen(false);
  };

  const formatPhone = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{4})\d+?$/, '$1');
  };

  const isContactPhoneComplete = contactForm.phone.replace(/\D/g, '').length === 11;

  return (
    <div className="min-h-screen bg-white" data-testid="claim-page">
      <SEOHead
        title="Reivindicar perfil | BuscaDoutor"
        description="Fluxo seguro para reivindicar e validar um perfil profissional no BuscaDoutor."
        canonicalUrl="/claim"
        robots="noindex, nofollow"
      />
      <Navbar />
      <section className="bg-gradient-to-br from-[#F4FAF8] via-white to-[#F4FAF8] py-12 md:py-16">
        <div className="container-app max-w-3xl">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#59706A] hover:text-primary mb-6 text-sm transition-colors"
            data-testid="claim-back-link"
          >
            <ChevronLeft className="w-4 h-4" />
            Voltar
          </Link>

          {/* Steps progress */}
          <div className="flex items-center gap-2 mb-8" data-testid="claim-stepper">
            {[1, 2, 3].map((n) => (
              <div key={n} className="flex-1 flex items-center gap-2">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                    step >= n ? 'bg-primary text-white' : 'bg-slate-200 text-slate-500'
                  }`}
                >
                  {step > n ? <CheckCircle2 className="w-4 h-4" /> : n}
                </div>
                {n < 3 && <div className={`flex-1 h-1 rounded ${step > n ? 'bg-primary' : 'bg-slate-200'}`} />}
              </div>
            ))}
          </div>

          {step === 1 && (
            <div data-testid="claim-step-search">
              <h1
                className="text-3xl md:text-4xl font-bold text-[#1A2421] mb-3 leading-tight"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Encontre sua clínica ou consultório
              </h1>
              <p className="text-[#59706A] mb-8">
                Busque pelo seu nome ou pelo telefone do consultório. Vamos confirmar sua
                identidade pelo WhatsApp em seguida. (Caso não tenha um telefone cadastrado,
                entre em{' '}
                <button
                  type="button"
                  onClick={() => setContactModalOpen(true)}
                  className="font-semibold text-primary underline underline-offset-4 transition-colors hover:text-[#2D6D5D]"
                  data-testid="claim-contact-link"
                >
                  contato conosco
                </button>
                )
              </p>

              <div className="relative mb-6">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Ex: Dra Maria Silva ou (11) 99999-9999"
                  className="h-14 pl-12 text-base rounded-xl border-slate-200"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  data-testid="claim-search-input"
                />
              </div>

              {searching && (
                <div className="flex items-center gap-2 text-sm text-[#59706A]">
                  <Loader2 className="w-4 h-4 animate-spin" /> Buscando...
                </div>
              )}
              {searchError && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg p-3" data-testid="claim-search-error">
                  <AlertCircle className="w-4 h-4" /> {searchError}
                </div>
              )}

              <div className="space-y-3 mt-4">
                {results.map((r) => (
                  <div
                    key={r.id}
                    className="bg-white rounded-2xl border border-slate-200 p-5 hover:border-primary/30 hover:shadow-md transition-all"
                    data-testid={`claim-result-${r.id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-[#1A2421] text-lg leading-tight">{r.full_name}</h3>
                        {r.specialty_name && (
                          <p className="text-sm text-primary mt-1 inline-flex items-center gap-1.5">
                            <Stethoscope className="w-3.5 h-3.5" />
                            {r.specialty_name}
                          </p>
                        )}
                        {(r.address || r.city) && (
                          <p className="text-sm text-[#59706A] mt-1 inline-flex items-center gap-1.5">
                            <MapPin className="w-3.5 h-3.5 shrink-0" />
                            {[r.address, r.city, r.state].filter(Boolean).join(', ')}
                          </p>
                        )}
                      </div>
                      <div className="shrink-0">
                        {r.has_phone ? (
                          <Button
                            onClick={() => handleSelect(r)}
                            className="rounded-full bg-primary hover:bg-[#2D6D5D] text-white px-5"
                            data-testid={`claim-select-btn-${r.id}`}
                          >
                            Este é meu perfil
                          </Button>
                        ) : (
                          <span className="text-xs text-slate-400 italic">Sem telefone para verificação</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {!searching && query.length >= 2 && results.length === 0 && !searchError && (
                  <div className="text-center py-10 text-[#59706A]">
                    <p className="mb-2">Nenhum perfil encontrado.</p>
                    <p className="mt-4 flex flex-col items-center justify-center gap-3 text-sm sm:flex-row">
                      Não encontrou? <Link to="/cadastro?type=provider" className="inline-flex items-center justify-center rounded-full bg-accent px-5 py-2.5 text-sm font-bold text-accent-foreground no-underline shadow-[0_8px_24px_rgba(245,166,35,0.24)] transition-colors hover:bg-[#E89715] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60" data-testid="claim-register-provider-link">Cadastre-se gratuitamente em 2 minutos</Link>
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 2 && selected && (
            <div data-testid="claim-step-verify">
              <h1 className="text-3xl md:text-4xl font-bold text-[#1A2421] mb-3 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Verificar identidade
              </h1>
              <p className="text-[#59706A] mb-2">
                Vamos confirmar que você é o responsável por <strong>{selected.full_name}</strong>.
              </p>
              <div className="flex items-center gap-2 text-sm text-primary bg-primary/5 rounded-lg p-3 mb-6">
                <MessageCircle className="w-4 h-4" />
                Enviamos um código para o WhatsApp <strong>{contactMasked}</strong>
              </div>

              <form onSubmit={handleVerify} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-[#1A2421] mb-2 block">Código de 6 dígitos</label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="000000"
                    className="h-14 text-2xl text-center font-mono tracking-[0.5em] rounded-xl"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    data-testid="claim-otp-input"
                    autoFocus
                  />
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#59706A]">
                    {secondsLeft > 0 ? <>Expira em <strong>{expiresLabel}</strong></> : <span className="text-red-500">Código expirado</span>}
                  </span>
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={resendCooldown > 0}
                    className="inline-flex items-center gap-1 text-primary hover:underline disabled:text-slate-400 disabled:no-underline disabled:cursor-not-allowed"
                    data-testid="claim-resend-btn"
                  >
                    <RotateCw className="w-3.5 h-3.5" />
                    {resendCooldown > 0 ? `Reenviar em ${resendCooldown}s` : 'Reenviar código'}
                  </button>
                </div>

                {verifyError && (
                  <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg p-3" data-testid="claim-verify-error">
                    <AlertCircle className="w-4 h-4" /> {verifyError}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={verifying || otp.length !== 6 || secondsLeft === 0}
                  className="w-full h-12 rounded-full bg-primary hover:bg-[#2D6D5D] text-white"
                  data-testid="claim-verify-submit"
                >
                  {verifying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verificando...</> : 'Verificar código'}
                </Button>

                <button
                  type="button"
                  onClick={() => { setStep(1); setOtp(''); setSelected(null); setTokenId(null); }}
                  className="block w-full text-sm text-[#59706A] hover:text-primary"
                >
                  ← Voltar para a busca
                </button>
              </form>
            </div>
          )}

          {step === 3 && (
            <div data-testid="claim-step-register">
              <h1 className="text-3xl md:text-4xl font-bold text-[#1A2421] mb-3 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Crie sua conta
              </h1>
              <p className="text-[#59706A] mb-6">
                Identidade confirmada! Defina seu acesso para gerenciar o perfil de <strong>{selected?.full_name}</strong>.
              </p>

              <form onSubmit={handleFinalize} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-[#1A2421] mb-2 block">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="email"
                      required
                      autoComplete="email"
                      placeholder="seu@email.com"
                      className="h-12 pl-11 rounded-xl"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="claim-email-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-[#1A2421] mb-2 block">Senha (mínimo 8 caracteres)</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="password"
                      required
                      autoComplete="new-password"
                      minLength={8}
                      className="h-12 pl-11 rounded-xl"
                      value={pwd}
                      onChange={(e) => setPwd(e.target.value)}
                      data-testid="claim-password-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-[#1A2421] mb-2 block">Confirmar senha</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="password"
                      required
                      autoComplete="new-password"
                      minLength={8}
                      className="h-12 pl-11 rounded-xl"
                      value={pwd2}
                      onChange={(e) => setPwd2(e.target.value)}
                      data-testid="claim-password2-input"
                    />
                  </div>
                </div>

                {registerError && (
                  <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg p-3" data-testid="claim-register-error">
                    <AlertCircle className="w-4 h-4" /> {registerError}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={submitting || !email || pwd.length < 8 || pwd !== pwd2}
                  className="w-full h-12 rounded-full bg-primary hover:bg-[#2D6D5D] text-white"
                  data-testid="claim-finalize-submit"
                >
                  {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Criando conta...</> : 'Criar conta e acessar'}
                </Button>
              </form>

              <p className="text-xs text-[#59706A]/80 mt-6 inline-flex items-start gap-2">
                <ShieldCheck className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                Seus dados são protegidos e a senha é armazenada com criptografia.
              </p>
            </div>
          )}
        </div>
      </section>
      <Dialog open={contactModalOpen} onOpenChange={setContactModalOpen}>
        <DialogContent className="max-h-[85vh] max-w-[92vw] overflow-y-auto rounded-2xl sm:max-w-md" data-testid="claim-contact-modal">
          <DialogHeader>
            <DialogTitle>Contato para reivindicar perfil</DialogTitle>
            <DialogDescription>
              Informe seus dados e vamos continuar o atendimento pelo WhatsApp.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleContactSubmit} className="space-y-4">
            <Input
              required
              placeholder="Nome Profissional (Clínica ou Doutor)"
              value={contactForm.professionalName}
              onChange={(e) => setContactForm((form) => ({ ...form, professionalName: e.target.value }))}
              data-testid="claim-contact-name"
            />
            <SearchableCombobox
              open={contactSpecialtyOpen}
              onOpenChange={setContactSpecialtyOpen}
              items={contactSpecialties}
              selectedValue={contactForm.specialtyId}
              onSelect={(specialty) => setContactForm((form) => ({
                ...form,
                specialtyId: specialty.id,
                specialtyName: specialty.name,
              }))}
              onClear={() => setContactForm((form) => ({ ...form, specialtyId: '', specialtyName: '' }))}
              placeholder={contactLoading ? 'Carregando especialidades...' : 'Especialidade'}
              searchPlaceholder="Buscar especialidade..."
              emptyMessage="Nenhuma especialidade encontrada."
              icon={Stethoscope}
              disabled={contactLoading || contactSpecialties.length === 0}
              displayValue={contactForm.specialtyName || null}
              getItemKey={(item) => item.id}
              getItemLabel={(item) => item.name}
              getItemSearchValue={(item) => item.name}
              isSelected={(item) => item.id === contactForm.specialtyId}
              testId="claim-contact-specialty"
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <SearchableCombobox
                open={contactStateOpen}
                onOpenChange={setContactStateOpen}
                items={contactStates}
                selectedValue={contactForm.state}
                onSelect={(state) => setContactForm((form) => ({
                  ...form,
                  state: state.uf,
                  stateName: state.name,
                  city: '',
                }))}
                onClear={() => setContactForm((form) => ({ ...form, state: '', stateName: '', city: '' }))}
                placeholder={contactLoading ? 'Carregando estados...' : 'Estado'}
                searchPlaceholder="Buscar estado..."
                emptyMessage="Nenhum estado encontrado."
                icon={MapPin}
                disabled={contactLoading || contactStates.length === 0}
                displayValue={contactForm.state ? `${contactForm.stateName || contactForm.state} (${contactForm.state})` : null}
                getItemKey={(item) => item.uf}
                getItemLabel={(item) => `${item.name} (${item.uf})`}
                getItemSearchValue={(item) => `${item.name} ${item.uf}`}
                isSelected={(item) => item.uf === contactForm.state}
                testId="claim-contact-state"
              />
              <SearchableCombobox
                open={contactCityOpen}
                onOpenChange={setContactCityOpen}
                items={contactCities}
                selectedValue={contactForm.city}
                onSelect={(city) => setContactForm((form) => ({ ...form, city: city.city }))}
                onClear={() => setContactForm((form) => ({ ...form, city: '' }))}
                placeholder={!contactForm.state ? 'Selecione o estado' : (contactLoadingCities ? 'Carregando cidades...' : 'Cidade')}
                searchPlaceholder="Buscar cidade..."
                emptyMessage="Nenhuma cidade encontrada."
                icon={MapPin}
                disabled={!contactForm.state || contactLoadingCities || contactCities.length === 0}
                displayValue={contactForm.city || null}
                getItemKey={(item) => item.city}
                getItemLabel={(item) => item.city}
                getItemSearchValue={(item) => item.city}
                isSelected={(item) => item.city === contactForm.city}
                testId="claim-contact-city"
              />
            </div>
            <Input
              required
              inputMode="tel"
              maxLength={15}
              placeholder="Telefone (WhatsApp) com DDD"
              value={contactForm.phone}
              onChange={(e) => setContactForm((form) => ({ ...form, phone: formatPhone(e.target.value) }))}
              data-testid="claim-contact-phone"
            />
            {contactForm.phone && !isContactPhoneComplete && (
              <p className="-mt-2 text-xs text-red-600" data-testid="claim-contact-phone-error">
                Informe o WhatsApp completo com DDD. Ex: (71) 99999-9999
              </p>
            )}
            <Input
              required
              type="email"
              placeholder="Email"
              value={contactForm.email}
              onChange={(e) => setContactForm((form) => ({ ...form, email: e.target.value }))}
              data-testid="claim-contact-email"
            />
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setContactModalOpen(false)}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={
                  !contactForm.professionalName
                  || !contactForm.specialtyId
                  || !contactForm.state
                  || !contactForm.city
                  || !isContactPhoneComplete
                  || !contactForm.email
                }
                className="bg-primary text-white hover:bg-[#2D6D5D]"
              >
                Enviar pelo WhatsApp
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
}
